'use client';

import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { MASONRY_ITEM_ATTRIBUTE, useMasonry } from './use-masonry';
import styles from './styles.module.scss';

export interface FeedMasonryProps {
  className?: string;
  children?: React.ReactNode;
  onClickCapture?: React.MouseEventHandler<HTMLDivElement>;
  onFocusCapture?: React.FocusEventHandler<HTMLDivElement>;
}

/**
 * PC 피드 목록 컨테이너. 직계 자식인 `FeedMasonryItem`을 masonry로 배치한다(1280px 이상 3열, 769–1279px 2열).
 * 모바일(768px 이하)에서는 `display: contents`라 기존 1열 목록 흐름을 그대로 쓴다.
 */
const FeedMasonry = forwardRef<HTMLDivElement, FeedMasonryProps>(({
  className = '',
  children,
  onClickCapture,
  onFocusCapture,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);
  useMasonry(containerRef, { readyClassName: styles.ready });

  return (
    <div
      ref={containerRef}
      className={`${styles.masonry} ${className}`}
      onClickCapture={onClickCapture}
      onFocusCapture={onFocusCapture}
    >
      {children}
    </div>
  );
});

FeedMasonry.displayName = 'FeedMasonry';

export interface FeedMasonryItemProps extends React.HTMLAttributes<HTMLDivElement> {
  feedId?: string;
}

export const FeedMasonryItem = ({ feedId, className = '', children, ...rest }: FeedMasonryItemProps) => (
  <div
    {...rest}
    {...{ [MASONRY_ITEM_ATTRIBUTE]: '' }}
    className={`${styles.item} ${className}`}
    data-feed-id={feedId}
  >
    {children}
  </div>
);

export default FeedMasonry;
